import { useState, useEffect, useMemo } from "react";
import { faSearch, faCheck } from "@fortawesome/free-solid-svg-icons";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";

// ── Edit modal (simple form for editing an existing experiment metric) ─────

const CATEGORY_OPTIONS = ['Primary', 'Secondary', 'Guardrail'];

export const MetricEditModal = ({ isActive, editMetric, isSubmitting, onClose, onSubmit }) => {
    const [form, setForm] = useState({ label: '', eventKey: '', category: 'Secondary' });

    useEffect(() => {
        if (!isActive || !editMetric) return;
        setForm({ label: editMetric.label ?? '', eventKey: editMetric.eventKey ?? '', category: editMetric.category ?? 'Secondary' });
    }, [isActive, editMetric]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const isValid = form.label.trim() && form.eventKey.trim();

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className="modal-card-head">
                    <p className="modal-card-title">Edit Metric</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    <div className="field">
                        <label className="label">Label</label>
                        <input className="input" type="text" value={form.label}
                               onChange={e => set('label', e.target.value)} />
                    </div>
                    <div className="field">
                        <label className="label">Event Key</label>
                        <input className="input" type="text" value={form.eventKey}
                               onChange={e => set('eventKey', e.target.value)} />
                    </div>
                    <div className="field">
                        <label className="label">Category</label>
                        <div className="select is-fullwidth">
                            <select value={form.category} onChange={e => set('category', e.target.value)}>
                                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                </section>
                <footer className="modal-card-foot">
                    <Button className="is-link mr-2" onClick={() => isValid && onSubmit(form)}
                            disabled={isSubmitting || !isValid}>
                        {isSubmitting ? 'Saving…' : 'Update'}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Add modal (catalog picker) ──────────────────────────────────────────────

const categoryForCatalogMetric = (m) => {
    if (m.isGuardrail) return 'Guardrail';
    if (m.isCertified) return 'Primary';
    return null; // must be explicitly chosen by user
};

const matches = (val, q) => val && String(val).toLowerCase().includes(q);

export const MetricAddModal = ({ isActive, existingMetricIds, existingMetrics, isSubmitting, onClose, onSubmit }) => {
    const { execute } = useFetchWithMsal();
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(false);
    const [catalogError, setCatalogError] = useState(false);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [categoryOverrides, setCategoryOverrides] = useState({});

    const hasPrimary = useMemo(
        () => (existingMetrics ?? []).some(m => ['Primary', 'PRIMARY'].includes(m.category)),
        [existingMetrics]
    );

    useEffect(() => {
        if (!isActive) return;
        setQuery('');
        setSelected(new Set());
        setCategoryOverrides({});
        setCatalogError(false);
        setLoading(true);
        execute('GET', thor_api_urls.metricsRepo)
            .then(data => setCatalog(data?.metrics ?? []))
            .catch(() => { setCatalog([]); setCatalogError(true); })
            .finally(() => setLoading(false));
    }, [isActive, execute]);

    const filtered = useMemo(() => {
        const q = query.toLowerCase().trim();
        const all = catalog.filter(m => !existingMetricIds?.has(m.id));
        if (!q) return all;
        return all.filter(m =>
            matches(m.label, q) || matches(m.name, q) || matches(m.description, q)
        );
    }, [catalog, query, existingMetricIds]);

    const toggle = (id) => setSelected(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const resolvedCategory = (m) => categoryOverrides[m.id] ?? categoryForCatalogMetric(m);

    const selectedMissingType = useMemo(() =>
        catalog.filter(m => selected.has(m.id) && resolvedCategory(m) === null).length > 0,
        [catalog, selected, categoryOverrides] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Check if the batch about to be submitted would add a second Primary
    const batchWouldAddPrimary = useMemo(() => {
        return catalog
            .filter(m => selected.has(m.id))
            .some(m => resolvedCategory(m) === 'Primary');
    }, [catalog, selected, categoryOverrides]); // eslint-disable-line react-hooks/exhaustive-deps

    const showPrimaryWarning = hasPrimary && batchWouldAddPrimary;

    const handleSubmit = () => {
        const batch = catalog
            .filter(m => selected.has(m.id))
            .map(m => ({ metricId: m.id, label: m.label, eventKey: m.name, category: resolvedCategory(m) }));
        onSubmit(batch);
    };

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card" style={{ width: '780px', maxWidth: '95vw' }}>
                <header className="modal-card-head">
                    <p className="modal-card-title">Add Metrics from Catalog</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    <p className="help mb-3">Select 1 Primary · 1–6 Guardrail · 2–4 Secondary (optional).</p>
                    {loading ? <SmallLoadingSpinner /> : catalogError ? (
                    <div className="notification is-danger is-light">
                        The metrics catalog is unavailable right now. Please try again or contact your administrator.
                    </div>
                ) : (
                        <>
                            <div className="field mb-3">
                                <div className="control has-icons-left">
                                    <input className="input" type="text" placeholder="Search by label, name, or description…"
                                           value={query} onChange={e => setQuery(e.target.value)} autoFocus />
                                    <span className="icon is-left"><Icon icon={faSearch} /></span>
                                </div>
                            </div>
                            <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                                <table className="table is-fullwidth is-narrow is-hoverable" style={{ fontSize: '0.875rem', tableLayout: 'fixed' }}>
                                    <colgroup>
                                        <col style={{ width: '2rem' }} />
                                        <col style={{ width: '45%' }} />
                                        <col />
                                        <col style={{ width: '130px' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th />
                                            <th>Label</th>
                                            <th>Name / Event Key</th>
                                            <th>Type</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.length === 0
                                            ? <tr><td colSpan={4} className="has-text-centered has-text-grey py-3">No metrics found.</td></tr>
                                            : filtered.map(m => {
                                                const isSelected = selected.has(m.id);
                                                const cat = resolvedCategory(m);
                                                return (
                                                    <tr key={m.id}
                                                        onClick={() => toggle(m.id)}
                                                        style={{ cursor: 'pointer', background: isSelected ? '#f0f4ff' : undefined }}>
                                                        <td className="has-text-centered" style={{ verticalAlign: 'middle' }}>
                                                            {isSelected && <Icon icon={faCheck} className="has-text-link" />}
                                                        </td>
                                                        <td className="has-text-weight-semibold">
                                                            {m.label}
                                                            {m.isCertified && <span className="tag is-primary is-light is-small ml-1">Certified</span>}
                                                            {m.isGuardrail && <span className="tag is-warning is-light is-small ml-1">Guardrail</span>}
                                                            {hasPrimary && cat === 'Primary' && isSelected && (
                                                                <span className="tag is-warning is-small ml-1">Already have a Primary</span>
                                                            )}
                                                        </td>
                                                        <td className="has-text-grey is-family-monospace" style={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</td>
                                                                        <td onClick={e => e.stopPropagation()}>
                                                            <div className={`select is-small is-fullwidth${isSelected && cat === null ? ' is-danger' : ''}`}>
                                                                <select
                                                                    value={cat ?? ''}
                                                                    onChange={e => setCategoryOverrides(prev => ({ ...prev, [m.id]: e.target.value }))}>
                                                                    {cat === null && <option value="" disabled>Select type…</option>}
                                                                    <option value="Primary">Primary</option>
                                                                    <option value="Secondary">Secondary</option>
                                                                    {m.isGuardrail && <option value="Guardrail">Guardrail</option>}
                                                                </select>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                            <p className="has-text-grey is-size-7 mt-2">
                                {filtered.length} metric{filtered.length !== 1 ? 's' : ''}
                                {selected.size > 0 && ` — ${selected.size} selected`}
                            </p>
                            {showPrimaryWarning && (
                                <div className="notification is-warning is-light mt-2" style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}>
                                    Warning: You already have a Primary metric. Adding another may overwrite it.
                                </div>
                            )}
                        </>
                    )}
                </section>
                <footer className="modal-card-foot">
                    <Button className="is-link mr-2" onClick={handleSubmit}
                            disabled={isSubmitting || selected.size === 0 || selectedMissingType}
                            title={selectedMissingType ? 'Select a type for each chosen metric' : undefined}>
                        {isSubmitting ? 'Adding…' : `Add ${selected.size > 0 ? selected.size : ''} Metric${selected.size !== 1 ? 's' : ''}`}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// Default export kept for backward compat
export default MetricAddModal;
